import type { TFunction } from "i18next"

import { formatXpNumber } from "@/lib/format-xp"

/**
 * OKS-938 — Reward amount atoms for the mission card (UX Spec §3).
 *
 * An "atom" is a single formatted amount — `+25 XP`, `+5 coins`. Lines are
 * built by joining the atoms that are present with " · " (XP first) and
 * interpolating the result into a line key as `{{amounts}}`:
 *
 *   `+25 XP · +5 coins if you post by Aug 10`
 *   `+50 XP once your post reaches 5,000 views`
 *
 * ## Why compose atoms instead of giving each line its own placeholders
 *
 * Zero-suppression has to apply INDEPENDENTLY to every amount (a brand may
 * legitimately zero any single reward — `Rewards::Resolver` discriminates a
 * configured `0` from `nil`, and the ledger's CHECK forbids zero-amount
 * earns). Composing means an absent amount is simply not in the join, so no
 * line can render "+0" and no separator can dangle — the same code path
 * covers base, on-time and engagement rather than three hand-written
 * variants of the same rule.
 *
 * ## ⚠️ Why `count` and `amount` are BOTH passed, and why that lives here
 *
 * They look redundant and are not:
 *   - `count` must be a real number — i18next selects `_one` / `_other` off
 *     it. Pass a formatted string and pluralisation silently breaks.
 *   - `amount` carries the locale-grouped string (en `1,250` / fr `1 250`).
 *     Plain `{{count}}` interpolation does NOT format — it stringifies to
 *     `1250` in both locales.
 *
 * i18next's built-in `{{count, number}}` formatter would collapse the two,
 * but it is unreachable from this codebase: locale source is Rails YAML and
 * `inertia_i18n` rewrites interpolation with `/%\{(\w+)\}/` (converter.rb),
 * whose `\w+` cannot match `count, number` — the token would never be
 * rewritten and would reach the creator verbatim.
 *
 * So the pairing is unavoidable, and it lives in THIS ONE FUNCTION on
 * purpose. No call site ever sees `count` and `amount` together, which means
 * there is no site at which the "redundant" one can be tidied away into a
 * silent singular/plural bug.
 *
 * ## ⚠️ `locale` is required, deliberately
 *
 * `formatXpNumber`'s own `locale` parameter is OPTIONAL and defaults to
 * `"fr"`, so a call site that forgets it renders `1 250` under English while
 * every French assertion still passes — an en-only defect a fr-first suite
 * structurally cannot see. Requiring it here removes the trap for every
 * caller rather than relying on each one to remember.
 */

/** The two currencies the card states separately. Never blended into a total. */
export type RewardAtomKind = "xp" | "coins"

/**
 * One formatted amount, or `null` when the amount is not worth stating.
 *
 * @param t      i18next translate function.
 * @param kind   `"xp"` or `"coins"` — selects the atom key + noun.
 * @param amount Resolved amount from the wire. `<= 0` yields `null`.
 * @param locale BCP-47 tag (pass `i18n.language`) — REQUIRED, see above.
 * @returns e.g. `"+25 XP"`, `"+1 coin"`, or `null`.
 */
export function rewardAtom(
  t: TFunction,
  kind: RewardAtomKind,
  amount: number,
  locale: string,
): string | null {
  // Guards the wire's Error state too (UX Spec §5): a missing/NaN value must
  // never reach the card as "NaN" or an empty row.
  if (!Number.isFinite(amount) || amount <= 0) return null

  return t(`missions.card.reward.${kind}`, {
    count: amount,
    amount: formatXpNumber(amount, locale),
  })
}

/**
 * Join the present atoms into the `{{amounts}}` fragment of a reward line.
 *
 * XP first, then coins — a fixed order so the card reads the same way on
 * every mission, including when a bonus exceeds the base.
 *
 * @returns the joined fragment, or `null` when NO amount is worth stating —
 *   which is the caller's signal to render no line at all (rather than an
 *   empty row or a dangling separator).
 */
export function composeRewardAmounts(
  t: TFunction,
  amounts: { xp?: number; coins?: number },
  locale: string,
): string | null {
  const atoms = [
    rewardAtom(t, "xp", amounts.xp ?? 0, locale),
    rewardAtom(t, "coins", amounts.coins ?? 0, locale),
  ].filter((atom): atom is string => atom !== null)

  return atoms.length > 0 ? atoms.join(" · ") : null
}
